import React, {
  useState,
  useEffect,
  type ChangeEvent,
  type FormEvent,
} from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  InputAdornment,
  MenuItem,
  type SelectChangeEvent,
  Card,
  Divider,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import CustomTextField from "../../../components/common/Textfield";
import PhoneNumberField from "../../../components/common/PhoneNumberField";
import PhotoEditor from "../../../components/PhotoEditor";
import { MuiTelInputInfo } from "mui-tel-input";

export interface IContactData {
  id: number;
  contact_type: "individual";
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  country: string;
  website: string;
  notes: string;
  facebook_profile: string;
  instagram_profile: string;
  twitter_profile: string;
  linkedin_profile: string;
  contact_image: string | null;
  contactImageFile: File | null;
}

interface IContactResponse {
  id: number;
  [key: string]: any;
}

interface IEditIndividualContactProps {
  contact: IContactData;
  onComplete: (updatedContact: IContactData) => void;
  onCancel: () => void;
}

const DialogEditVenueIndividualContacts: React.FC<
  IEditIndividualContactProps
> = ({ contact, onComplete, onCancel }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [serverResponse, setServerResponse] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [emailError, setEmailError] = useState("");

  const [form, setForm] = useState<IContactData>({
    ...contact,
    email: contact.email ?? "",
    phone: contact.phone ?? "",
    website: contact.website ?? "",
    facebook_profile: contact.facebook_profile ?? "",
    instagram_profile: contact.instagram_profile ?? "",
    twitter_profile: contact.twitter_profile ?? "",
    linkedin_profile: contact.linkedin_profile ?? "",
    notes: contact.notes ?? "",
    contactImageFile: null,
  });

  const validateEmail = (email: string): string => {
    if (!email) return "";
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!regex.test(email)) return "Invalid email format.";
    if (email.startsWith(".")) return "Email cannot start with a dot.";
    if (email.endsWith(".")) return "Email cannot end with a dot.";
    if (email.split("@")[0].endsWith("."))
      return "Local part cannot end with a dot.";
    return "";
  };

  useEffect(() => {
    return () => {
      if (form.contact_image && form.contact_image.startsWith("blob:")) {
        URL.revokeObjectURL(form.contact_image);
      }
    };
  }, [form.contact_image]);

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    if (name === "email") {
      const error = validateEmail(value);
      setEmailError(error);
      setForm((prev) => ({ ...prev, email: value }));
    } else {
      setForm((prev) => ({ ...prev, [name as keyof IContactData]: value }));
    }
  };

  const handleSelectChangeWrapper = (event: any) => {
    if (event && event.target && typeof event.target.value !== "string") {
      event.target.value = String(event.target.value);
    }
    handleChange(event);
  };

  const handlePhoneInputChange = (value: string, info: MuiTelInputInfo) => {
    setForm((prev) => ({ ...prev, phone: value }));
  };

  const handleSaveContactImage = async (
    imageData: string,
    contactId?: number
  ) => {
    try {
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], `contact_image_${contactId || "new"}.jpg`, {
        type: blob.type,
      });
      setForm((prev) => ({
        ...prev,
        contact_image: imageData,
        contactImageFile: file,
      }));
      showSuccessToast("Contact image saved successfully!");
    } catch (error) {
      console.error("Failed to process contact image:", error);
      showErrorToast("Failed to process contact image. Please try again.");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!id) return;
    setIsSubmitting(true);
    setServerResponse(null);

    if (!form.first_name || !form.last_name) {
      setServerResponse({
        success: false,
        message: "First name and last name are required.",
      });
      showErrorToast("First name and last name are required.");
      setIsSubmitting(false);
      return;
    }

    if (!form.email && !form.phone) {
      setServerResponse({
        success: false,
        message: "Provide at least one contact method (email or phone number).",
      });
      showErrorToast(
        "Provide at least one contact method (email or phone number)."
      );
      setIsSubmitting(false);
      return;
    }

    if (emailError) {
      setServerResponse({
        success: false,
        message: "Please correct the email address error.",
      });
      showErrorToast("Please correct the email address error.");
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("contact_type", form.contact_type);
    Object.entries(form).forEach(([key, value]) => {
      if (key === "contactImageFile" && value instanceof File) {
        formData.append("contact_image", value);
      } else if (key !== "contact_image" && value !== null && value !== "") {
        formData.append(key, value.toString());
      }
    });

    try {
      const response = await apiService.patch<IContactResponse>(
        `/api/contacts/contact/${form.id}/`,
        formData,
        false
      );

      setServerResponse({
        success: true,
        message: "Individual contact updated successfully.",
      });
      showSuccessToast("Individual contact updated successfully.");

      const updatedContact: IContactData = { ...form }; // include all current form fields
      onComplete(updatedContact);
    } catch (err: any) {
      console.error("Failed to update individual contact:", err);
      let errorMessage =
        "Failed to update individual contact. Please try again.";
      if (err.response?.data) {
        errorMessage =
          Object.values(err.response.data).flat().join(" ") || errorMessage;
      }
      setServerResponse({
        success: false,
        message: errorMessage,
      });
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEmailValid = !emailError && (form.email ?? "").includes("@");

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": { borderColor: reduxTheme.primary_colors?.[300] || "#ddd" },
      "&:hover fieldset": {
        borderColor: reduxTheme.primary_colors?.[300] || "#bbb",
      },
      "&.Mui-focused fieldset": {
        borderColor: reduxTheme.primary_colors?.[400] || "#b8865b",
      },
    },
    "& .MuiInputLabel-root": {
      color: reduxTheme.primary_colors?.[300] || "#666",
    },
    "& .MuiInputBase-input": {
      color: reduxTheme.neutral_colors?.[100] || "#333",
    },
  };

  return (
    <Card
      sx={{
        maxWidth: 1200,
        mx: "auto",
        p: 4,
        borderRadius: 3,
        boxShadow: 0,
        background: reduxTheme.neutral_colors?.[900] || "#fff",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          color: reduxTheme.primary_colors?.[400] || "#614438",
          fontWeight: 600,
          mb: 3,
        }}
      >
        Edit Individual Contact
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: reduxTheme.primary_colors?.[400] || "#614438",
              fontWeight: 600,
              mb: 3,
            }}
          >
            Personal Details
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="First Name"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                required
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                required
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {isEmailValid && (
                        <Check
                          sx={{
                            color:
                              reduxTheme.primary_colors?.[400] || "#b8865b",
                          }}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ...textFieldStyles,
                  "& .MuiFormHelperText-root": {
                    color: reduxTheme.neutral_colors?.[100] || "#333",
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <PhoneNumberField
                fullWidth
                label="Phone Number"
                value={form.phone}
                onChange={handlePhoneInputChange}
                defaultCountry="ZW"
                variant="outlined"
                sx={{ marginBottom: "15px" }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Website"
                name="website"
                value={form.website}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <PhotoEditor
                initialImage={form.contact_image}
                title="Profile Photo"
                onSave={(imageData: string) =>
                  handleSaveContactImage(imageData)
                }
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: reduxTheme.primary_colors?.[400] || "#614438",
              fontWeight: 600,
              mb: 3,
            }}
          >
            Location Info
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Address Line 1"
                name="address_one"
                value={form.address_one}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Address Line 2"
                name="address_two"
                value={form.address_two}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Suburb"
                name="suburb"
                value={form.suburb}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Country"
                name="country"
                value={form.country}
                onChange={handleSelectChangeWrapper}
                select
                sx={textFieldStyles}
              >
                <MenuItem value="" disabled sx={{ color: "#aaa" }}>
                  Select Country
                </MenuItem>
                <MenuItem value="Zimbabwe">Zimbabwe</MenuItem>
                <MenuItem value="South Africa">South Africa</MenuItem>
                <MenuItem value="Zambia">Zambia</MenuItem>
                <MenuItem value="Malawi">Malawi</MenuItem>
                <MenuItem value="Botswana">Botswana</MenuItem>
                <MenuItem value="Namibia">Namibia</MenuItem>
              </CustomTextField>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: reduxTheme.primary_colors?.[400] || "#614438",
              fontWeight: 600,
              mb: 3,
            }}
          >
            Social Media
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Facebook Profile"
                name="facebook_profile"
                value={form.facebook_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="LinkedIn Profile"
                name="linkedin_profile"
                value={form.linkedin_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="X Profile"
                name="twitter_profile"
                value={form.twitter_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Instagram Profile"
                name="instagram_profile"
                value={form.instagram_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: reduxTheme.primary_colors?.[400] || "#614438",
              fontWeight: 600,
              mb: 3,
            }}
          >
            Additional Info
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <CustomTextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={4}
                value={form.notes}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{ display: "flex", justifyContent: "flex-start", gap: 2, mt: 4 }}
        >
          <Button
            variant="outlined"
            onClick={() => onCancel()} // <-- call onCancel instead of onComplete
            disabled={isSubmitting}
            sx={{
              borderColor: reduxTheme.primary_colors?.[400] || "#614438",
              color: reduxTheme.primary_colors?.[400] || "#614438",
              "&:hover": {
                borderColor: reduxTheme.primary_colors?.[300] || "#7a5a4a",
                backgroundColor: reduxTheme.primary_colors?.[50] || "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: reduxTheme.primary_colors?.[400] || "#614438",
              "&:hover": {
                backgroundColor: reduxTheme.primary_colors?.[300] || "#7a5a4a",
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress
                size={24}
                sx={{ color: reduxTheme.neutral_colors?.[100] || "#fff" }}
              />
            ) : (
              "Save Contact"
            )}
          </Button>
        </Box>
      </form>
    </Card>
  );
};

export default DialogEditVenueIndividualContacts;
